import styled from 'styled-components';

import { useSortable } from '@dnd-kit/react/sortable';

import Button from 'components/shared/Button';
import DragHandle from 'components/shared/DragHandle';
import FieldLabel from 'components/shared/form/FieldLabel';
import Input from 'components/shared/form/Input';
import { WorkflowLookupField, WorkflowLookupItem } from 'models/WorkflowLookup';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

import LookupItemFields from './LookupItemFields';
import { isCustomized } from './lookupItemHelpers';

type Props = {
  item: WorkflowLookupItem;
  index: number;
  fields: WorkflowLookupField[];
  parentOptions: Record<string, WorkflowLookupItem[]>;
  parentsResolved: boolean;
  // False for display text, whose entries come from the workflow: no adding, no archiving, and no
  // reordering either, since that order is the contract's rather than anyone's preference.
  itemsEditable: boolean;
  // Alphabetical order is computed on read, so hand-ordering is meaningless while it is on.
  alphabetized: boolean;
  canEdit: boolean;
  onChange: (changes: Partial<WorkflowLookupItem>) => void;
  // Removing an entry that has never been saved drops it outright. Archiving it instead left a
  // blank entry in the payload, which the API rejects for having no short name — and because it was
  // hidden from the list, nothing on this screen could reach it to fix. There is no history to
  // protect on an entry that was never saved.
  onDiscard: () => void;
};

export default function LookupItemRow({
  item,
  index,
  fields,
  parentOptions,
  parentsResolved,
  itemsEditable,
  alphabetized,
  canEdit,
  onChange,
  onDiscard,
}: Props) {
  const { ref, handleRef, isDragSource } = useSortable({
    id: item._key ?? String(index),
    index,
    disabled: !itemsEditable || !canEdit || alphabetized,
  });

  const disabled = !canEdit;

  // A field pointing into another list is the entry's grouping, so it is asked first; everything
  // else the list declares follows the name.
  const parentFields = fields.filter((field) => field.type === 'lookup');
  const ownFields = fields.filter((field) => field.type !== 'lookup');

  return (
    <Row ref={ref} $archived={item.archived} style={{ opacity: isDragSource ? 0.5 : 1 }}>
      {/* No spacer when there is no handle: an empty column indented every field on lists that
          cannot be reordered, which is all of the display-text ones. */}
      {itemsEditable && canEdit && !alphabetized && <DragHandle ref={handleRef} />}

      <Fields>
        {/* The field that says which list this entry belongs under comes first, matching the order
            the product asks for it in: the deny modal picks a Denial Type before a Reason. */}
        <LookupItemFields
          item={item}
          fields={parentFields}
          parentOptions={parentOptions}
          parentsResolved={parentsResolved}
          disabled={disabled}
          onChange={onChange}
        />

        <Field>
          <FieldLabel>Name</FieldLabel>
          <Input
            disabled={disabled}
            value={item.displayName}
            placeholder='Name shown to users'
            onChange={(event) => onChange({ displayName: event.target.value })}
          />
          {/* Shown once it exists, never editable: reports group by it, so renaming would split one
              thing's history in two. A new entry takes it from the name typed above. */}
          {item.apiName && <ShortName>{item.apiName}</ShortName>}
        </Field>

        <LookupItemFields
          item={item}
          fields={ownFields}
          parentOptions={parentOptions}
          parentsResolved={parentsResolved}
          disabled={disabled}
          onChange={onChange}
        />

        {isCustomized(item) && (
          <Drifted>
            <span>Default: {item.sourceDisplayName}</span>
            {canEdit && (
              <Button
                variant='ghost'
                size='xsmall'
                onClick={() =>
                  onChange({ displayName: item.sourceDisplayName ?? '', description: item.sourceDescription })
                }>
                Restore
              </Button>
            )}
          </Drifted>
        )}
      </Fields>

      {itemsEditable && canEdit && (
        <Button
          variant='ghost'
          size='small'
          onClick={() => (item._isNew ? onDiscard() : onChange({ archived: !item.archived }))}>
          {item.archived ? 'Restore' : 'Remove'}
        </Button>
      )}
    </Row>
  );
}

const Row = styled.div<{ $archived: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid ${colors.black10};
  opacity: ${({ $archived }) => ($archived ? 0.55 : 1)};
`;

const Fields = styled.div`
  flex: 1;
`;

const Field = styled.div`
  margin-bottom: 16px;
`;

const ShortName = styled(BodySmall)`
  display: block;
  margin-top: 4px;
  /* Lines up with the text inside the input above it (8px padding + 1px border), not with the
     input's edge. The "Name" label above sits flush because it reads as a label, not as a value. */
  padding-left: 9px;
  color: ${colors.black50};
`;

const Drifted = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${colors.black75};
  font-size: 12px;
`;
